import { useEffect, useRef, useState } from 'react';
import { useWorkspaceContext } from 'src/providers/WorkspaceProvider/Workspace.context';
import { WalletService } from '../services/Wallet.service';
import type { tSendCostEstimate } from '../Wallet.types';

/**
 * Cuánto se espera tras el último cambio de la selección antes de cotizar.
 *
 * No es un número de gusto. Para un workspace SIN movimientos en el ledger —el
 * caso normal antes de la primera recarga, o sea justo el de este ticket— cada
 * cotización deriva la moneda contra Stripe, y eso consume el mismo cubo de
 * rate limit que el cobro de la recarga. El contrato del endpoint pide
 * explícitamente que el front acote la frecuencia en vez de que el back cachee
 * la moneda, para no meter una segunda fuente de verdad de lo que el ledger
 * estampa. 450 ms es más que una pausa de escritura y menos que la percepción
 * de lentitud.
 */
const ESTIMATE_DEBOUNCE_MS = 450;

export type tSendCostEstimateState = {
  estimate: tSendCostEstimate | null;
  isEstimating: boolean;
  /** Verdadero cuando la cotización falló. El consumidor NO pinta un cero. */
  hasFailed: boolean;
};

const IDLE: tSendCostEstimateState = {
  estimate: null,
  isEstimating: false,
  hasFailed: false,
};

/**
 * Cotiza un envío mientras el usuario arma la selección (RET-1694).
 *
 * Qué resuelve, y por qué cada cosa está donde está:
 *
 *   - **Debounce** (arriba): una llamada por pausa, no una por tecla.
 *   - **Sin cotización imposible:** con `category` en null (un `template_type`
 *     que no sabemos traducir) o sin destinatarios, NO se llama. El endpoint
 *     rechaza una lista vacía con 400, así que llamar sería fabricar un error
 *     evitable; y una categoría desconocida cotizaría a una tarifa adivinada.
 *   - **Un fallo NO es un cero.** `hasFailed` existe para que el consumidor
 *     pueda decir "no pudimos calcularlo" en vez de pintar `$0.00`, que antes
 *     de un envío se lee como "es gratis".
 *   - **Carrera de respuestas:** cada petición lleva un número de secuencia y
 *     sólo la última escribe estado. Sin eso, cotizar 500 destinatarios y luego
 *     quitar 499 puede terminar mostrando el total de los 500 si la primera
 *     respuesta llega después — un precio que no corresponde a la selección que
 *     el usuario está viendo.
 *   - **Recotizar sin cambiar la selección** (RET-2327): `requoteToken`
 *     (opcional) entra en las dependencias del efecto para que un llamador
 *     pueda pedir una cotización nueva de la MISMA selección — un chat no
 *     cambia de categoría ni de destinatario al enviar, pero el saldo que el
 *     servidor ve sí pudo moverse.
 *
 * El resultado NO se cachea entre montajes a propósito: el catálogo es
 * effective-dated y el estimado es un techo, así que un estimado guardado y
 * presentado como firme es exactamente lo que el contrato prohíbe.
 */
export function useSendCostEstimate(params: {
  category: string | null;
  recipients: string[];
  /**
   * Permite apagar el hook sin desmontar al consumidor: con la wallet apagada
   * por flag, o con una audiencia que no sabemos resolver a teléfonos (una
   * campaña por etapa o tag), no hay nada que cotizar y tampoco hay que llamar.
   */
  enabled: boolean;
  /**
   * RET-2327 (C-3), OPCIONAL: sube cuando algo —fuera de la selección misma—
   * pudo mover el saldo y amerita recotizar. Entra en las dependencias del
   * efecto: cambiarlo vuelve a cotizar la MISMA selección con la MISMA
   * disciplina (debounce, secuencia, cancelación) de abajo; el mismo token no
   * dispara nada nuevo. Es un hook compartido: el composer lo sube con eventos
   * del chat, y `CampaignEstimatedCost` cuando cambia el saldo (RET-2429).
   */
  requoteToken?: number;
}): tSendCostEstimateState {
  const { currentWorkspace } = useWorkspaceContext();
  const workspaceId = currentWorkspace?.id ?? '';

  const [state, setState] = useState<tSendCostEstimateState>(IDLE);

  // La lista se compara por su contenido, no por identidad: el consumidor la
  // construye con .map() en cada render, así que una dependencia por referencia
  // dispararía una cotización por render. El separador es un carácter que no
  // aparece en un teléfono normalizado (sólo dígitos).
  const recipientsKey = params.recipients.join(',');
  const { category, enabled, requoteToken } = params;

  const sequence = useRef(0);

  useEffect(() => {
    const recipients = recipientsKey ? recipientsKey.split(',') : [];

    if (!enabled || !workspaceId || !category || recipients.length === 0) {
      sequence.current += 1;
      setState(IDLE);

      return;
    }

    const attempt = (sequence.current += 1);
    let cancelled = false;

    setState((previous) => ({ ...previous, isEstimating: true, hasFailed: false }));

    const timer = setTimeout(() => {
      WalletService.estimateSendCost(workspaceId, { category, recipients })
        .then((estimate) => {
          // Sólo la última petición escribe. `cancelled` cubre el desmontaje;
          // la secuencia cubre la respuesta vieja que llega tarde.
          if (cancelled || attempt !== sequence.current) return;

          setState({ estimate, isEstimating: false, hasFailed: false });
        })
        .catch(() => {
          if (cancelled || attempt !== sequence.current) return;

          // El error no se propaga ni se loguea: el cuerpo de esta petición son
          // teléfonos (PII) y cualquier reporte automático arrastraría la
          // petición completa. El consumidor sólo necesita saber que no hay
          // número que pintar.
          setState({ estimate: null, isEstimating: false, hasFailed: true });
        });
    }, ESTIMATE_DEBOUNCE_MS);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // `requoteToken` (RET-2327, C-3): vuelve a cotizar la MISMA selección. La
    // escritura de "en vuelo" de arriba conserva `previous.estimate`, así que
    // un token nuevo no parpadea la cotización anterior mientras la nueva
    // viaja. (El `IDLE` y el `catch` sí la limpian, pero esos no los dispara
    // un token: el primero sólo corre con la cotización apagada.)
  }, [workspaceId, category, recipientsKey, enabled, requoteToken]);

  return state;
}

/**
 * Cotiza el costo de UN mensaje por categoría, para el selector de plantillas
 * del chat 1 a 1 (RET-1694, pantalla 10).
 *
 * Por qué no es `useSendCostEstimate` por plantilla: el selector lista N
 * plantillas y cada renglón muestra su costo. Cotizar plantilla por plantilla
 * serían N viajes —con 30 plantillas, 30 llamadas, cada una potencialmente un
 * hit a Stripe—. No hace falta: con UN destinatario el precio depende sólo de
 * **categoría × país**, y el país es fijo (el teléfono del lead). Las
 * categorías de Meta son cuatro, así que basta **una llamada por categoría
 * distinta presente en la lista**: como máximo cuatro, normalmente dos.
 *
 * La caché se llavea por `(teléfono, categoría)` y se vacía al cambiar de
 * conversación, porque el precio depende del país del destinatario.
 */
export function useTemplateCostByCategory(params: {
  recipient: string;
  categories: readonly string[];
  enabled: boolean;
}): {
  costByCategory: Partial<Record<string, tSendCostEstimate>>;
  isEstimating: boolean;
} {
  const { currentWorkspace } = useWorkspaceContext();
  const workspaceId = currentWorkspace?.id ?? '';

  const [costByCategory, setCostByCategory] = useState<
    Partial<Record<string, tSendCostEstimate>>
  >({});
  const [pending, setPending] = useState(0);

  const { recipient, enabled } = params;
  // Ordenada y única: el consumidor deriva las categorías de una lista que se
  // reordena al filtrar, y sin esto un reordenamiento re-cotizaría lo mismo.
  const categoriesKey = Array.from(new Set(params.categories)).sort().join(',');

  /*
    Lo ya pedido vive en un REF, no en el estado, y esa es la decisión que hace
    correcto a este hook.

    La versión con estado tenía una carrera real: un effect limpiaba la caché al
    cambiar de destinatario y otro decidía qué pedir leyendo esa caché, pero el
    segundo veía el valor del render en curso —todavía con los precios del lead
    anterior—, concluía que no faltaba nada, y en el render siguiente sus
    dependencias no habían cambiado, así que no volvía a correr. Resultado: al
    cambiar de conversación el precio no se recotizaba nunca. Lo cazó
    `useSendCostEstimate.test.tsx`, no la revisión a ojo.

    Un ref no participa del ciclo render→effect, así que "qué ya pedí" se lee
    siempre actualizado y no hay que declararlo como dependencia.
  */
  const requested = useRef<{ recipient: string; categories: Set<string> }>({
    recipient: '',
    categories: new Set(),
  });

  useEffect(() => {
    if (!enabled || !workspaceId || !recipient || !categoriesKey) return;

    // Cambió el destinatario: los precios del anterior no sirven (el precio es
    // por país) y hay que volver a empezar.
    if (requested.current.recipient !== recipient) {
      requested.current = { recipient, categories: new Set() };
      setCostByCategory({});
    }

    const missing = categoriesKey.split(',').filter(
      (category) => !requested.current.categories.has(category),
    );

    if (missing.length === 0) return;

    for (const category of missing) requested.current.categories.add(category);

    setPending((count) => count + missing.length);

    for (const category of missing) {
      WalletService.estimateSendCost(workspaceId, {
        category,
        recipients: [recipient],
      })
        .then((estimate) => {
          // Una respuesta que llega después de cambiar de conversación se
          // descarta: pintaría el precio del país del lead anterior.
          if (requested.current.recipient !== recipient) return;

          setCostByCategory((previous) => ({ ...previous, [category]: estimate }));
        })
        // Una categoría que no se pudo cotizar simplemente no aparece en el
        // mapa, y su renglón cae al copy de siempre. No se pinta un cero:
        // "gratis" es una afirmación sobre dinero que no podemos hacer.
        .catch(() => undefined)
        .finally(() => {
          setPending((count) => Math.max(count - 1, 0));
        });
    }
  }, [enabled, workspaceId, recipient, categoriesKey]);

  return { costByCategory, isEstimating: pending > 0 };
}
